import type { Clock, IdGenerator } from "../ports/clock";
import type { FoodRepository, RecipeRepository } from "../ports/repositories";
import {
  DEFAULT_RECIPE_SERVING_NAME,
  snapshotRecipeIngredient,
  validateRecipe,
  type Recipe,
} from "../../domain/recipes/recipe";

export interface SaveRecipeInput {
  readonly id?: string;
  readonly name: string;
  readonly servingName?: string;
  readonly servingCount: number;
  readonly ingredients: readonly {
    readonly foodId: string;
    /** Quantity in the saved food's native basis unit. */
    readonly amount: number;
  }[];
}

export async function saveRecipe(
  dependencies: {
    readonly foods: FoodRepository;
    readonly recipes: RecipeRepository;
    readonly clock: Clock;
    readonly ids: IdGenerator;
  },
  input: SaveRecipeInput,
): Promise<Recipe> {
  const existing = input.id ? await dependencies.recipes.getById(input.id) : undefined;
  if (input.id && !existing) throw new Error("Recipe not found");
  const ingredients = await Promise.all(input.ingredients.map(async (ingredient) => {
    const food = await dependencies.foods.getById(ingredient.foodId);
    if (!food) throw new Error(`Food not found: ${ingredient.foodId}`);
    return snapshotRecipeIngredient(food, { amount: ingredient.amount, unit: food.basis.unit });
  }));
  const now = dependencies.clock.now();
  const recipe = validateRecipe({
    id: existing?.id ?? dependencies.ids.next(),
    name: input.name,
    servingName: input.servingName ?? DEFAULT_RECIPE_SERVING_NAME,
    servingCount: input.servingCount,
    ingredients,
    createdAt: existing?.createdAt ?? now,
    updatedAt: now,
  });
  await dependencies.recipes.save(recipe);
  return recipe;
}
